/* eslint-disable react/destructuring-assignment */
import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Avatar
} from '@material-ui/core';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import SearchIcon from '@material-ui/icons/Search';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { questionnaireApi } from '../../../api/questionnaireApi';
import { APPRENANT_PATH } from '../questionnaire/responses/constants';
import useMounted from '../../../hooks/useMounted';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, grey } from '../../../theme/tones';

// Trois phases suivies, alignées sur les types de questionnaires
// (Pré-Formation, Post-Formation, Satisfaction).
// `type` fait le pont avec l'onglet correspondant de « Résultats des
// questionnaires », où l'on renvoie l'utilisateur.
const PHASES = [
  { key: 'pre', label: 'Pré', type: 'Pré-Formation', color: getTone('emerald').base, bg: toneAlpha('emerald', 0.12) },
  { key: 'post', label: 'Post', type: 'Post-Formation', color: getTone('orange').base, bg: toneAlpha('orange', 0.12) },
  { key: 'satisfaction', label: 'Satisfaction', type: 'Satisfaction', color: getTone('indigo').base, bg: toneAlpha('indigo', 0.12) }
];

// 4e colonne, affichée seulement quand la formation de la session a un
// questionnaire de validation (certificat de décès, 248 et 254) — le serveur
// le signale par `validation_enabled`.
const VALIDATION_PHASE = { key: 'validation', label: 'Validation', type: 'Validation', color: getTone('cyan').color, bg: toneAlpha('cyan', 0.12) };

const getInitials = (firstname, lastname) => {
  const f = (firstname || '').trim();
  const l = (lastname || '').trim();
  return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || '?';
};

const SessionResponsesTracking = (props) => {
  const { sessionId, formationId, sessionNumber } = props;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Vrai si la formation de la session a un questionnaire de validation.
  const [validationEnabled, setValidationEnabled] = useState(false);
  const [search, setSearch] = useState('');
  // Relance : l'apprenant visé, ce que le serveur propose d'envoyer, et l'état
  // des deux appels (aperçu puis envoi).
  const [target, setTarget] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [sending, setSending] = useState(false);
  // Relance en masse : le même aller-retour aperçu puis envoi, mais pour tous
  // les apprenants qui n'ont pas répondu.
  const [bulkPlan, setBulkPlan] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const mounted = useMounted();

  const getTracking = useCallback(async () => {
    setLoading(true);
    try {
      const response = await questionnaireApi.getResponsesTrackingBySession(
        sessionId,
        localStorage.getItem('accessToken')
      );
      if (mounted.current) {
        setRows(response && response.status && Array.isArray(response.data) ? response.data : []);
        setValidationEnabled(Boolean(response && response.validation_enabled));
      }
    } catch (err) {
      console.error(err);
      if (mounted.current) setRows([]);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [sessionId, mounted]);

  useEffect(() => {
    getTracking();
  }, [getTracking]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.firstname || ''} ${r.lastname || ''}`.toLowerCase().includes(q)
      || (r.email || '').toLowerCase().includes(q));
  }, [rows, search]);

  /**
   * Le serveur reste seul juge de ce qui manque : le bouton demande d'abord un
   * aperçu (rien n'est envoyé), le dialogue le montre, et seule la confirmation
   * déclenche l'e-mail.
   */
  const openSend = async (row) => {
    setTarget(row);
    setPlan(null);
    setPlanLoading(true);
    try {
      const res = await questionnaireApi.missingQuestionnaires(
        sessionId,
        row.customer_id,
        localStorage.getItem('accessToken'),
        true
      );
      if (!mounted.current) return;
      if (res && res.status) {
        setPlan(res);
      } else {
        toast.error((res && res.message) || 'Relance impossible.');
        setTarget(null);
      }
    } catch (err) {
      console.error(err);
      if (mounted.current) {
        toast.error('Erreur technique.');
        setTarget(null);
      }
    } finally {
      if (mounted.current) setPlanLoading(false);
    }
  };

  const confirmSend = async () => {
    if (!target) return;
    setSending(true);
    try {
      const res = await questionnaireApi.missingQuestionnaires(
        sessionId,
        target.customer_id,
        localStorage.getItem('accessToken'),
        false
      );
      if (res && res.status) {
        toast.success(res.message || 'Relance envoyée.');
        setTarget(null);
        setPlan(null);
        getTracking();
      } else {
        toast.error((res && res.message) || "L'envoi a échoué.");
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur technique.');
    } finally {
      if (mounted.current) setSending(false);
    }
  };

  /**
   * Relance en masse. Le serveur reste seul juge de qui doit être relancé et
   * de ce qui manque à chacun : l'aperçu n'envoie rien, il montre la liste.
   */
  const openBulkSend = async () => {
    setBulkPlan(null);
    setBulkLoading(true);
    try {
      const res = await questionnaireApi.missingQuestionnairesForSession(
        sessionId,
        localStorage.getItem('accessToken'),
        true
      );
      if (!mounted.current) return;
      if (res && res.status) {
        setBulkPlan(res);
      } else {
        toast.error((res && res.message) || 'Aucun apprenant à relancer.');
      }
    } catch (err) {
      console.error(err);
      if (mounted.current) toast.error('Erreur technique.');
    } finally {
      if (mounted.current) setBulkLoading(false);
    }
  };

  const confirmBulkSend = async () => {
    setBulkSending(true);
    try {
      const res = await questionnaireApi.missingQuestionnairesForSession(
        sessionId,
        localStorage.getItem('accessToken'),
        false
      );
      if (res && res.status) {
        toast.success(res.message || 'Relances envoyées.');
        setBulkPlan(null);
        getTracking();
      } else {
        toast.error((res && res.message) || "L'envoi a échoué.");
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur technique.');
    } finally {
      if (mounted.current) setBulkSending(false);
    }
  };

  // Une réponse de validation déjà enregistrée garde sa colonne, même si le
  // questionnaire a été retiré depuis.
  const phases = useMemo(
    () => (validationEnabled || rows.some((r) => r.validation) ? [...PHASES, VALIDATION_PHASE] : PHASES),
    [validationEnabled, rows]
  );

  const totals = useMemo(() => {
    const acc = {};
    phases.forEach((p) => { acc[p.key] = 0; });
    rows.forEach((r) => {
      phases.forEach((p) => {
        if (r[p.key]) acc[p.key] += 1;
      });
    });
    return acc;
  }, [rows, phases]);

  /**
   * Qui reste à relancer, d'après les pastilles déjà affichées : sert à
   * l'état du bouton et à son libellé. Le serveur refait le calcul avant
   * d'envoyer — lui seul sait si le questionnaire manquant existe.
   */
  const pending = useMemo(() => {
    const incomplete = rows.filter((r) => !phases.every((p) => r[p.key]));
    return {
      total: incomplete.length,
      withEmail: incomplete.filter((r) => r.email).length
    };
  }, [rows, phases]);

  /**
   * « Résultats des questionnaires » ne connaît que les sessions ayant reçu au
   * moins une réponse : sans réponse, la session n'y existe pas et le lien
   * mènerait à une liste où elle est absente. On n'affiche donc le raccourci
   * qu'une fois la première réponse arrivée.
   *
   * Le type visé est celui d'un onglet où la session apparaît réellement —
   * pointer « Satisfaction » alors que seul le Pré est rempli ouvrirait un
   * écran vide.
   */
  const resultsLink = useMemo(() => {
    if (!formationId) return null;
    // Validation (si la formation en a une), Satisfaction, Post puis Pré : on
    // ouvre l'onglet le plus avancé parmi ceux qui ont effectivement des réponses.
    const phase = [...phases].reverse().find((p) => totals[p.key] > 0);
    if (!phase) return null;

    const params = new URLSearchParams({
      type: phase.type,
      formation: String(formationId),
      session: String(sessionId)
    });
    return `${APPRENANT_PATH}?${params.toString()}`;
  }, [formationId, sessionId, totals, phases]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card
        sx={{
          borderRadius: '20px',
          overflow: 'hidden',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: (theme) => `0 4px 6px -1px ${alpha(theme.palette.common.black, 0.1)}, 0 2px 4px -1px ${alpha(theme.palette.common.black, 0.06)}`,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${theme.palette.background.default} 100%)`
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.7),
            backdropFilter: 'blur(10px)',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.tones.indigo.base} 100%)`,
                boxShadow: (theme) => `0 4px 10px -2px ${alpha(theme.palette.secondary.main, 0.4)}`
              }}
            >
              <FactCheckRoundedIcon sx={{ color: grey(0) }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                Suivi des réponses
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={`${rows.length} ${rows.length > 1 ? 'apprenants' : 'apprenant'}`}
                  size="small"
                  sx={{ bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12), color: 'secondary.main', fontWeight: 800, borderRadius: '8px' }}
                />
                {phases.map((p) => (
                  <Chip
                    key={p.key}
                    label={`${p.label} ${totals[p.key]}/${rows.length}`}
                    size="small"
                    sx={{ bgcolor: p.bg, color: p.color, fontWeight: 800, borderRadius: '8px' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Rechercher un apprenant..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="outlined"
              sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'background.paper' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
            />

            {/* Un seul geste pour toute la session : l'envoi individuel
                reste sur chaque ligne, celui-ci s'adresse à tous ceux qui
                n'ont pas fini de répondre. */}
            {!loading && rows.length > 0 && (
              <Tooltip
                arrow
                title={pending.withEmail === 0
                  ? (pending.total === 0
                    ? 'Tous les apprenants ont répondu'
                    : "Aucun des apprenants à relancer n'a d'adresse e-mail")
                  : `Relancer les ${pending.withEmail} apprenant${pending.withEmail > 1 ? 's' : ''} qui n'${pending.withEmail > 1 ? 'ont' : 'a'} pas répondu à tout`}
              >
                <span>
                  <Button
                    variant="contained"
                    disabled={pending.withEmail === 0 || bulkLoading}
                    onClick={openBulkSend}
                    startIcon={bulkLoading
                      ? <CircularProgress size={14} color="inherit" />
                      : <SendRoundedIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 800,
                      whiteSpace: 'nowrap',
                      background: `linear-gradient(135deg, ${getTone('indigo').base} 0%, ${getTone('indigo').color} 100%)`
                    }}
                  >
                    {`Relancer les non-répondants${pending.withEmail > 0 ? ` (${pending.withEmail})` : ''}`}
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Tant que rien n'est rempli, la session n'existe pas dans
                « Résultats des questionnaires » : on le dit au lieu d'offrir un
                lien qui mènerait à une liste où elle est absente. */}
            {!loading && (resultsLink ? (
              <Tooltip title={`Ouvrir les réponses de la session${sessionNumber ? ` n° ${sessionNumber}` : ''} dans Résultats des questionnaires`}>
                <Button
                  component={RouterLink}
                  to={resultsLink}
                  variant="outlined"
                  startIcon={<InsightsRoundedIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                    borderColor: 'divider',
                    color: 'text.primary',
                    bgcolor: 'background.paper',
                    '&:hover': { borderColor: getTone('indigo').base, bgcolor: toneAlpha('indigo', 0.06) }
                  }}
                >
                  Voir dans les résultats
                </Button>
              </Tooltip>
            ) : (
              <Chip
                size="small"
                label="Pas encore de réponses"
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.text.disabled, 0.1),
                  color: 'text.secondary',
                  fontWeight: 800,
                  borderRadius: '8px',
                  height: 30,
                  px: 0.5
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Contenu */}
        {loading ? (
          <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      borderBottom: (theme) => `2px solid ${theme.palette.divider}`
                    }}
                  >
                    Apprenant
                  </TableCell>
                  {phases.map((p) => (
                    <TableCell
                      key={p.key}
                      align="center"
                      sx={{
                        fontWeight: 800,
                        color: p.color,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
                        width: 140
                      }}
                    >
                      {p.label}
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 800,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
                      width: 150
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow
                    key={row.customer_id}
                    hover
                    sx={{ '& .MuiTableCell-root': { borderBottom: (theme) => `1px solid ${theme.palette.background.default}` } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: '0.8rem',
                            fontWeight: 800,
                            bgcolor: (theme) => alpha(theme.palette.tones.indigo.base, 0.12),
                            color: getTone('indigo').base
                          }}
                        >
                          {getInitials(row.firstname, row.lastname)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9rem', lineHeight: 1.2 }}>
                            {`${row.firstname || ''} ${row.lastname || ''}`.trim() || 'Apprenant'}
                          </Typography>
                          {row.email && (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                              {row.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    {phases.map((p) => (
                      <TableCell key={p.key} align="center">
                        <Tooltip title={row[p.key] ? 'A répondu' : 'Pas de réponse'} arrow>
                          <Checkbox
                            checked={!!row[p.key]}
                            readOnly
                            disableRipple
                            icon={<RadioButtonUncheckedRoundedIcon sx={{ color: grey(300) }} />}
                            checkedIcon={<CheckCircleRoundedIcon sx={{ color: p.color }} />}
                            sx={{ cursor: 'default', p: 0.5 }}
                          />
                        </Tooltip>
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      {(() => {
                        // Rien à relancer si tout est répondu, rien à envoyer
                        // sans adresse : dans les deux cas le bouton reste
                        // visible mais désactivé, avec la raison en infobulle.
                        const done = phases.every((p) => row[p.key]);
                        const noEmail = !row.email;
                        const disabled = done || noEmail || planLoading;
                        let reason = 'Envoyer les questionnaires manquants';
                        if (done) reason = 'A répondu à tout';
                        else if (noEmail) reason = "Pas d'adresse e-mail";

                        return (
                          <Tooltip title={reason} arrow>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={disabled}
                                onClick={() => openSend(row)}
                                startIcon={
                                  planLoading && target && target.customer_id === row.customer_id
                                    ? <CircularProgress size={13} color="inherit" />
                                    : <SendRoundedIcon sx={{ fontSize: 16 }} />
                                }
                                sx={{
                                  borderRadius: '10px',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  borderColor: 'divider',
                                  color: getTone('indigo').color
                                }}
                              >
                                Envoyer
                              </Button>
                            </span>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={phases.length + 2} align="center" sx={{ py: 8, borderBottom: 'none' }}>
                      <Box sx={{ opacity: 0.6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <FactCheckRoundedIcon sx={{ fontSize: 42, color: 'text.disabled' }} />
                        <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                          {rows.length === 0
                            ? 'Aucun apprenant inscrit pour cette session.'
                            : 'Aucun apprenant ne correspond à la recherche.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Relance en masse : même principe, mais la liste complète des
          destinataires — et de ceux qui ne recevront rien, avec la raison. */}
      <Dialog
        open={Boolean(bulkPlan)}
        onClose={() => { if (!bulkSending) setBulkPlan(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: 'text.primary', pb: 1 }}>
          Relancer les non-répondants
        </DialogTitle>
        <DialogContent>
          {bulkPlan && (
            <>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 2 }}>
                {bulkPlan.recipients.length > 1
                  ? `${bulkPlan.recipients.length} apprenants vont recevoir un e-mail`
                  : 'Un apprenant va recevoir un e-mail'}
                {bulkPlan.session_number ? ` · session n° ${bulkPlan.session_number}` : ''}
              </Typography>

              {bulkPlan.recipients.map((r) => (
                <Box
                  key={r.id}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    borderRadius: '12px',
                    border: `1px solid ${grey(100)}`,
                    backgroundColor: 'action.hover'
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'text.primary' }}>
                    {r.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: getTone('indigo').base, fontWeight: 600 }}>
                    {r.email}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                    {r.missing.map((m) => (
                      <Chip
                        key={m.key}
                        label={m.label}
                        size="small"
                        sx={{ bgcolor: alpha(m.color, 0.12), color: m.color, fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}

              {bulkPlan.skipped && bulkPlan.skipped.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', mb: 1 }}>
                    {`${bulkPlan.skipped.length} apprenant${bulkPlan.skipped.length > 1 ? 's' : ''} ne ${bulkPlan.skipped.length > 1 ? 'recevront' : 'recevra'} rien`}
                  </Typography>
                  {bulkPlan.skipped.map((r) => (
                    <Typography key={r.id} sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.35 }}>
                      <strong>{r.name}</strong>
                      {' — '}
                      {r.reason}
                    </Typography>
                  ))}
                </>
              )}

              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 1.5 }}>
                Un e-mail par apprenant, avec un bouton par questionnaire manquant.
                Ceux auxquels il a déjà répondu ne sont jamais inclus.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setBulkPlan(null)}
            disabled={bulkSending}
            sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
          >
            Annuler
          </Button>
          <Button
            onClick={confirmBulkSend}
            disabled={bulkSending}
            variant="contained"
            startIcon={bulkSending ? <CircularProgress size={14} color="inherit" /> : <SendRoundedIcon sx={{ fontSize: 17 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${getTone('indigo').base} 0%, ${getTone('indigo').color} 100%)`
            }}
          >
            {bulkSending ? 'Envoi en cours…' : 'Envoyer à tous'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation : on montre le destinataire et exactement ce qui partira. */}
      <Dialog
        open={Boolean(target && plan)}
        onClose={() => { if (!sending) { setTarget(null); setPlan(null); } }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: 'text.primary', pb: 1 }}>
          Relancer cet apprenant
        </DialogTitle>
        <DialogContent>
          {plan && (
            <>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                Un e-mail va être envoyé à
              </Typography>
              <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.95rem' }}>
                {plan.recipient.name}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: getTone('indigo').base, fontWeight: 600 }}>
                {plan.recipient.email}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', mb: 1 }}>
                {plan.missing.length > 1
                  ? `${plan.missing.length} questionnaires à remplir`
                  : 'Questionnaire à remplir'}
              </Typography>
              {plan.missing.map((m) => (
                <Box
                  key={m.questionnaire_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1.25,
                    mb: 1,
                    borderRadius: '12px',
                    border: `1px solid ${grey(100)}`,
                    backgroundColor: 'action.hover'
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: m.color, flexShrink: 0 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>
                    {m.label}
                  </Typography>
                </Box>
              ))}

              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 1.5 }}>
                Chaque questionnaire aura son propre bouton dans l’e-mail. Ceux
                auxquels il a déjà répondu ne sont pas inclus.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => { setTarget(null); setPlan(null); }}
            disabled={sending}
            sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
          >
            Annuler
          </Button>
          <Button
            onClick={confirmSend}
            disabled={sending}
            variant="contained"
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendRoundedIcon sx={{ fontSize: 17 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${getTone('indigo').base} 0%, ${getTone('indigo').color} 100%)`
            }}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

SessionResponsesTracking.propTypes = {
  sessionId: PropTypes.any.isRequired,
  /** Sans elle, pas de raccourci : « Résultats » s'ouvre au niveau formation. */
  formationId: PropTypes.any,
  /** Purement indicatif, pour l'infobulle du raccourci. */
  sessionNumber: PropTypes.any
};

export default SessionResponsesTracking;
